import React, { useState } from 'react';
import { AddressAutocomplete, LocationSelectData } from './AddressAutocomplete';
import { VenueAutocomplete } from './VenueAutocomplete';
import { PinOnMapPicker } from './PinOnMapPicker';

export interface AddressData {
  street: string;
  zip: string;
  locality: string;
  country: string;
  region: string;
}

export interface EventLocationData {
  name: string;
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  addressComponents?: AddressData;
}

interface EventLocationFormProps {
  value: EventLocationData;
  onChange: (location: EventLocationData) => void;
  showVenueName?: boolean;
  venueNameLabel?: string;
  venueNamePlaceholder?: string;
  addressPlaceholder?: string;
  className?: string;
}

export const EventLocationForm: React.FC<EventLocationFormProps> = ({
  value,
  onChange,
  showVenueName = true,
  venueNameLabel,
  venueNamePlaceholder = 'Venue name (optional)',
  addressPlaceholder = 'Start typing an address...',
  className = '',
}) => {
  const [showPinMap, setShowPinMap] = useState(false);

  const handleFieldChange = (field: keyof EventLocationData, fieldValue: string | { lat: number; lng: number }) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const handleAddressSelect = (location: LocationSelectData) => {
    onChange({
      name: location.name || value.name,
      address: location.address,
      coordinates: location.coordinates,
      addressComponents: location.addressComponents,
    });
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Venue name input with autocomplete */}
      {showVenueName && (
        <div>
          {venueNameLabel && (
            <label className="label block mb-1 text-sm">{venueNameLabel}</label>
          )}
          <VenueAutocomplete
            value={value.name}
            onChange={(name) => handleFieldChange('name', name)}
            onSelect={(location) => onChange(location)}
            placeholder={venueNamePlaceholder}
          />
        </div>
      )}

      {/* Address autocomplete */}
      <AddressAutocomplete
        value={value.address}
        onChange={(addr) => handleFieldChange('address', addr)}
        onSelect={handleAddressSelect}
        placeholder={addressPlaceholder}
      />

      {/* Coordinates display + pin on map toggle */}
      {value.coordinates.lat !== 0 && value.coordinates.lng !== 0 && (
        <div className="bg-gray-50 p-3 rounded-lg text-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-gray-600">
              <span>📍</span>
              <span>
                Coordinates: {value.coordinates.lat.toFixed(6)}, {value.coordinates.lng.toFixed(6)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowPinMap((v) => !v)}
              className="shrink-0 text-xs text-balloon hover:underline"
            >
              {showPinMap ? 'Close map' : 'Pin on map'}
            </button>
          </div>

          {showPinMap && (
            <div className="mt-2">
              <PinOnMapPicker
                coordinates={value.coordinates}
                onChange={(coords) =>
                  onChange({ ...value, coordinates: coords })
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
